<template>
  <div v-if="articles.length">
    <ArticleCardsCarousel
      v-if="layout === 'carousel'"
      :articles
      :cta
      :subtitle
      :title
      :tracking
    />

    <div v-if="layout === 'grid'">
      <ArticleCardsCarousel
        :articles
        class="block md:hidden"
        :cta
        :double="true"
        :subtitle
        :title
        :tracking
      />

      <TitleCtaContainer
        class="hidden md:block"
        :cta
        :secondary-layout="true"
        :subtitle
        :title
        :tracking="ctaTrackingData"
      >
        <div
          :class="[
            'mt-16 grid gap-16 md:grid-cols-3 lg:grid-cols-4',
            { [$style.clipCards]: clipCards },
          ]"
        >
          <div
            v-for="(article, index) in articles"
            v-show="!enableMask || index < HIDE_CARDS_AMOUNT || displayAllCards"
            :id="`article-card-${index}`"
            :key="article.id"
            class="hidden md:block"
          >
            <ArticleCard
              class="!w-auto"
              v-bind="article"
              :tracking="{
                ...tracking,
                position: index + 1,
                ...(props.title && { blockTitle: props.title }),
              }"
            />
          </div>
        </div>

        <div
          v-if="enableMask && articles.length > HIDE_CARDS_AMOUNT"
          class="mt-16 flex justify-center"
          :class="{ [$style.bottomGradient]: clipCards }"
        >
          <RevButton
            class="self-end"
            :icon="displayAllCards ? IconChevronUp : IconChevronDown"
            size="small"
            variant="primary"
            @click="displayButtonClick"
          >
            {{
              i18n(
                displayAllCards ? translations.seeLess : translations.seeMore,
              )
            }}
          </RevButton>
        </div>
      </TitleCtaContainer>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { useRuntimeConfig } from '#imports'
import { computed, ref } from 'vue'

import type { CTALink } from '@backmarket/http-api/src/api-specs-content/models/cta-link'
import type { Tracking } from '@backmarket/http-api/src/api-specs-content/models/tracking'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { isEmpty } from '@backmarket/utils/object/isEmpty'
import { RevButton } from '@ds/components/Button'
import { IconChevronDown } from '@ds/icons/IconChevronDown'
import { IconChevronUp } from '@ds/icons/IconChevronUp'

import type { ArticleCard as ArticleCardType } from '@backmarket/nuxt-layer-cms/ArticleCard/ArticleCard.types'

import ArticleCard from '../ArticleCard/ArticleCard.vue'
import ArticleCardsCarousel from '../ArticleCard/ArticleCardsCarousel.vue'
import TitleCtaContainer from '../TitleCtaContainer/TitleCtaContainer.vue'

import translations from './ArticleCardsCollection.translations'

const props = defineProps<{
  title?: string
  tracking?: Tracking
  subtitle?: string
  articles: ArticleCardType[]
  layout: 'grid' | 'carousel'
  cta?: CTALink
}>()

const i18n = useI18n()

const { FF_ENABLE_ARTICLE_CARD_GRID_MASK: enableMask } =
  useRuntimeConfig().public

const displayAllCards = ref(false)

const HIDE_CARDS_AMOUNT = 20

const clipCards = computed(() => {
  if (!enableMask) return false

  return props.articles.length > HIDE_CARDS_AMOUNT && !displayAllCards.value
})

const ctaTrackingData = computed(() => {
  if (isEmpty(props.tracking)) return undefined

  return {
    ...props.tracking,
    value: `${props.tracking.value}_CTA`,
  }
})

function displayButtonClick() {
  if (displayAllCards.value) {
    const element = document.getElementById('article-card-15')
    if (element) element.scrollIntoView()
  }

  displayAllCards.value = !displayAllCards.value
}
</script>

<style module>
.clipCards {
  @apply gradient-mask-b-80;
  clip-path: inset(0 0 168px 0);
}

.bottomGradient {
  @apply relative h-[112px] mt-[-280px] mb-56;
  background: linear-gradient(
    0deg,
    #f8f9fc 0,
    rgba(248, 249, 252, 0.7) 41.5%,
    rgba(248, 249, 252, 0) 100%
  );
}
</style>
